import { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Cronjob } from "../../../../../../../models/cronjob";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import CronjobService from "../../../../../../../services/cronjob.service";
import { CronjobKeys } from "../../../../../../../util/queries";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

const useActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const [cronjobs, setCronjobs] = useState<Cronjob[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const {
    data,
    refetch: refetchCronjobs,
    isFetching,
  } = useQuery({
    queryKey: CronjobKeys.getAndCountAll([page, pageSize, filters, searches, sorting]),
    queryFn: async () => {
      const response = await CronjobService.getAndCountAll({
        page,
        pageSize,
        groupBy: JSON.stringify(["cronjob.id"]),
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "cronjobContacts" }]),
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["createdAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      if (response && response.data) return response.data;
    },
    enabled: !!currentUser?.companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: CACHE_TIME,
  });

  const useUpdateCronjob = () => {
    return useMutation({
      mutationKey: CronjobKeys.update(),
      mutationFn: ({ id, cronjob }: { id: number; cronjob: Cronjob }) =>
        CronjobService.update(id, cronjob),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CronjobKeys.all], refetchType: "none" });
        refetchCronjobs();
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
    });
  };

  const useDeleteCronjob = () => {
    return useMutation({
      mutationKey: CronjobKeys.delete(),
      mutationFn: (id: number) => CronjobService.delete(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CronjobKeys.all], refetchType: "none" });
        refetchCronjobs();
        NotificationToast.success(translate("deletedSuccessfully"));
      },
    });
  };

  useEffect(() => {
    if (data && data?.rows) {
      setCronjobs(data.rows);
      setTotal(data.count);
    }
  }, [data]);

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const refetch = () => {
    refetchCronjobs();
  };

  return {
    variables: {
      cronjobs,
      page,
      pageSize,
      total,
      isFetching,
    },
    refetch,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    setPageSize,
    useUpdateCronjob,
    useDeleteCronjob,
  };
};

export default useActions;
